// Customer orders (M7, WP7.2): URL state, API calls and TanStack Query options for the order
// list, one order, taking and editing orders, their statuses and cancelling them. Every
// response is parsed with the shared @enp/domain/api schema (lib/api.ts).

import {
  type OrderCreateRequest,
  type OrderStatusRequest,
  type OrderUpdateRequest,
  ORDER_COVERAGE,
  ORDER_KINDS,
  catalogueProductListResponseSchema,
  feasibilityOrderResponseSchema,
  orderDetailSchema,
  orderListResponseSchema,
} from '@enp/domain/api';
import { isPlainDate } from '@enp/domain/time';
import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';
import { orderKeys } from '../planning/queries.js';
import { clean } from '../stock/queries.js';

const plainDate = z.string().refine(isPlainDate).optional().catch(undefined);

/** Which orders the list shows, by order status. */
export const ORDER_VIEWS = ['open', 'completed', 'cancelled', 'all'] as const;
export type OrderView = (typeof ORDER_VIEWS)[number];

/** The API's `status` for a view. */
export const VIEW_STATUSES: Record<OrderView, string> = {
  open: 'draft,confirmed',
  completed: 'completed',
  cancelled: 'cancelled',
  all: 'draft,confirmed,completed,cancelled',
};

/** The order list's state in the URL. Anything invalid is dropped rather than failing. */
export const ordersSearchSchema = z.object({
  q: z.string().trim().min(1).max(100).optional().catch(undefined),
  view: z.enum(ORDER_VIEWS).optional().catch(undefined),
  kind: z.enum(ORDER_KINDS).optional().catch(undefined),
  coverage: z.enum(ORDER_COVERAGE).optional().catch(undefined),
  from: plainDate,
  to: plainDate,
});
export type OrdersSearch = z.infer<typeof ordersSearchSchema>;

/** Rows per page. */
export const ORDERS_PAGE = 50;

export const ordersQuery = (search: OrdersSearch) =>
  infiniteQueryOptions({
    queryKey: [...orderKeys.all, 'list', clean(search)] as const,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({
        status: VIEW_STATUSES[search.view ?? 'open'],
        limit: String(ORDERS_PAGE),
      });
      if (search.q) params.set('q', search.q);
      if (search.kind) params.set('kind', search.kind);
      if (search.coverage) params.set('coverage', search.coverage);
      if (search.from) params.set('from', search.from);
      if (search.to) params.set('to', search.to);
      if (pageParam) params.set('cursor', pageParam);
      return api(`/orders?${params}`, { signal, schema: orderListResponseSchema });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: 60_000,
    retry: false,
  });

export const orderQuery = (id: string) =>
  queryOptions({
    queryKey: [...orderKeys.all, 'order', id] as const,
    queryFn: ({ signal }) =>
      api(`/orders/${encodeURIComponent(id)}`, { signal, schema: orderDetailSchema }),
    retry: false,
  });

/** Takes an order; one key per form, so a double click or a retry makes one (D-063). */
export const createOrder = (body: OrderCreateRequest, idempotencyKey: string) =>
  api('/orders', {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    schema: orderDetailSchema,
  });

export const updateOrder = (id: string, body: OrderUpdateRequest) =>
  api(`/orders/${encodeURIComponent(id)}`, { method: 'PUT', body, schema: orderDetailSchema });

export const changeOrderStatus = (id: string, body: OrderStatusRequest) =>
  api(`/orders/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    body,
    schema: orderDetailSchema,
  });

export const cancelOrder = (id: string, reason: string) =>
  api(`/orders/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: { reason },
    schema: orderDetailSchema,
  });

/** Sellable catalogue products by name or code (an order line's product picker). */
export const sellableSearchQuery = (q: string) =>
  queryOptions({
    queryKey: [...orderKeys.all, 'products', q] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ q, role: 'sellable', limit: '8' });
      return api(`/catalogue/products?${params}`, {
        signal,
        schema: catalogueProductListResponseSchema,
      });
    },
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });

/** The order's lines in its branch's current run, with the run's freshness and horizon. */
export const orderFeasibilityQuery = (id: string) =>
  queryOptions({
    queryKey: [...orderKeys.all, 'feasibility', id] as const,
    queryFn: ({ signal }) =>
      api(`/feasibility/orders/${encodeURIComponent(id)}`, {
        signal,
        schema: feasibilityOrderResponseSchema,
      }),
    refetchInterval: 30_000,
    retry: false,
  });
